import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { apiGet, apiPost, apiDelete } from "../../services/api/apiClient";
import { sendInvoicePaymentLink } from "../../services/api/paymentLinkService";

type LoadingStatus = "idle" | "loading" | "succeeded" | "failed";

interface InvoicesMessagesState {
  fetchInvoicesByProjectIdStatus: LoadingStatus;
  fetchInvoicesByProjectIdError: string | null;
  fetchInvoicesStatus: LoadingStatus;
  fetchInvoicesError: string | null;
  fetchPendingInvoicesStatus: LoadingStatus;
  fetchPendingInvoicesError: string | null;
  updateInvoiceStatus: LoadingStatus;
  updateInvoiceError: string | null;
  invoiceChargeStatus: LoadingStatus;
  invoiceChargeError: string | null;
  cartChargeStatus: LoadingStatus;
  cartChargeError: string | null;
  fetchAttachmentsStatus: LoadingStatus;
  fetchAttachmentsError: string | null;
  uploadFileStatus: LoadingStatus;
  uploadFileError: string | null;
  deleteFileStatus: LoadingStatus;
  deleteFileError: string | null;
  fetchDetailStatus: LoadingStatus;
  fetchDetailError: string | null;
}

interface InvoicesState extends InvoicesMessagesState {
  invoices: any[];
  pendingInvoices: any[];
  selectedInvoice: any | null;
  invoiceAttachments: any[];
  changes: any | null;
  isEditingState: boolean;
  filters: {
    invoices: { paymentStatus: string[] };
  };
  invoicesTotal: number;
  invoicesPage: number; // 0-based, matches the table
  invoicesPageSize: number;
}

const initialMessagesState: InvoicesMessagesState = {
  fetchInvoicesByProjectIdStatus: "idle",
  fetchInvoicesByProjectIdError: null,

  fetchInvoicesStatus: "idle",
  fetchInvoicesError: null,

  fetchPendingInvoicesStatus: "idle",
  fetchPendingInvoicesError: null,

  updateInvoiceStatus: "idle",
  updateInvoiceError: null,

  invoiceChargeStatus: "idle",
  invoiceChargeError: null,

  cartChargeStatus: "idle",
  cartChargeError: null,

  fetchAttachmentsStatus: "idle",
  fetchAttachmentsError: null,

  uploadFileStatus: "idle",
  uploadFileError: null,

  deleteFileStatus: "idle",
  deleteFileError: null,

  fetchDetailStatus: "idle",
  fetchDetailError: null,
};

const initialState: InvoicesState = {
  ...initialMessagesState,
  invoices: [],
  pendingInvoices: [],
  selectedInvoice: null,
  invoiceAttachments: [],
  changes: null,
  isEditingState: false,
  filters: {
    invoices: { paymentStatus: ["1"] },
  },
  invoicesTotal: 0,
  invoicesPage: 0,
  invoicesPageSize: 10,
};

export const fetchInvoicesByProjectId = createAsyncThunk(
  "cabinet/fetchInvoicesByProjectId",
  async (_, { rejectWithValue }) => {
    const userId = JSON.parse(localStorage.getItem("user") || "null")?.id;

    if (!userId) {
      return rejectWithValue("No user found");
    }
    try {
      const data = await apiGet({
        endpoint: "/get-invoices-by-project-id",
        params: { userId },
        withAuth: true,
      });
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch invoices");
    }
  },
);

export const fetchInvoices = createAsyncThunk(
  "invoices/fetchInvoices",
  async (params: any = {}, { rejectWithValue }) => {
    const userId = JSON.parse(localStorage.getItem("user") || "null")?.id;

    if (!userId) {
      return rejectWithValue("No user found");
    }
    try {
      const queryParams: any = { userId };

      // When page params are provided, the server returns a paginated
      // { items, page, pageSize, total } response; otherwise it returns the
      // full list (legacy behavior for personal-cabinet pages).
      if (params.page != null) {
        queryParams.page = params.page + 1; // 0-based -> 1-based
        queryParams.pageSize = params.pageSize || 10;
        if (params.paymentStatus?.length) {
          queryParams.paymentStatus = params.paymentStatus.join(",");
        }
        if (params.invoiceNumber) {
          queryParams.invoiceNumber = params.invoiceNumber;
        }
        if (params.customerName) {
          queryParams.customerName = params.customerName;
        }
        // Which accounting system to list. The switch in the navbar decides
        // this globally; the list just names it.
        if (params.source) {
          queryParams.source = params.source;
        }
        if (params.sortBy) {
          queryParams.sortBy = params.sortBy;
          queryParams.sortOrder = params.sortOrder || "asc";
        }
      }

      const data = await apiGet({
        endpoint: "/get-invoices",
        params: queryParams,
        withAuth: true,
      });
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch invoices");
    }
  },
);

// Downloads the payment links for whatever the list is currently showing, as a
// CSV the office works from: who to write to, what is owed, and the link. The
// server filters the same way the list does, so the file matches the screen.
export const exportPaymentLinks = createAsyncThunk(
  "invoices/exportPaymentLinks",
  async (params: any = {}, { rejectWithValue }) => {
    const queryParams = new URLSearchParams();
    if (params.paymentStatus?.length) {
      queryParams.set("paymentStatus", params.paymentStatus.join(","));
    }
    if (params.invoiceNumber) queryParams.set("invoiceNumber", params.invoiceNumber);
    if (params.customerName) queryParams.set("customerName", params.customerName);
    if (params.source) queryParams.set("source", params.source);
    // Which link the file's column carries. The rows stay the ones on screen
    // either way, so a file always matches the list it was exported from.
    if (params.link) queryParams.set("link", params.link);
    if (params.sortBy) {
      queryParams.set("sortBy", params.sortBy);
      queryParams.set("sortOrder", params.sortOrder || "asc");
    }

    const token = JSON.parse(localStorage.getItem("user") || "null")?.token;
    const headers: Record<string, string> = { Accept: "text/csv" };
    if (token) headers.Authorization = `Bearer ${token}`;

    try {
      const qs = queryParams.toString();
      const res = await fetch(
        `${import.meta.env.VITE_BASE_URL || ""}/export-payment-links${qs ? `?${qs}` : ""}`,
        { method: "GET", headers },
      );
      if (!res.ok) throw new Error("Failed to export payment links");

      // The filename the server chose, or a dated one of our own.
      const disposition = res.headers.get("Content-Disposition") || "";
      const named = disposition.match(/filename="?([^";]+)"?/);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = named?.[1] || `payment-links-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      return { success: true };
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to export payment links");
    }
  },
);

export const fetchPendingInvoices = createAsyncThunk(
  "dashboard/fetchPendingInvoices",
  async (_, { rejectWithValue }) => {
    const userId = JSON.parse(localStorage.getItem("user") || "null")?.id;

    // Token check is handled by apiClient with withAuth: true
    try {
      const data = await apiGet({
        endpoint: "/get-pending-invoices",
        params: { userId },
        withAuth: true,
      });
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch users");
    }
  },
);

export const setInvoiceVisibility = createAsyncThunk(
  "invoices/setInvoiceVisibility",
  async ({ providerId, isVisible }: any, { rejectWithValue }) => {
    try {
      const data = await apiPost({
        endpoint: "/set-invoice-visibility",
        data: { providerId, isVisible },
        withAuth: true,
      });
      return { ...data, providerId };
    } catch (error: any) {
      return rejectWithValue(
        error.message || "Failed to set invoice visibility",
      );
    }
  },
);

export const chargeInvoice = createAsyncThunk(
  "dashboard/chargeInvoice",
  async ({ customerId, invoiceId, accountId, amount }: any, { rejectWithValue }) => {
    // Token check is handled by apiClient with withAuth: true
    try {
      const data = await apiPost({
        endpoint: "/charge-invoice",
        data: {
          customerId,
          invoiceId,
          accountId,
          amount: amount == null ? amount : String(amount),
        },
        withAuth: true,
      });
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to charge invoice");
    }
  },
);

// Charge several invoices in a single payment (shopping cart).
export const chargeCart = createAsyncThunk(
  "invoices/chargeCart",
  async (
    { customerId, accountId, payments }: any,
    { rejectWithValue },
  ) => {
    try {
      const data = await apiPost({
        endpoint: "/cart/charge",
        data: { customerId, accountId, payments },
        withAuth: true,
      });
      return data;
    } catch (error: any) {
      return rejectWithValue(
        error.message || "Failed to process cart payment",
      );
    }
  },
);

export const fetchInvoiceAttachments = createAsyncThunk(
  "invoices/fetchInvoiceAttachments",
  async (invoiceId: string, { rejectWithValue }) => {
    try {
      const data = await apiGet({
        endpoint: "/get-invoice-attachments",
        params: { invoiceId },
        withAuth: true,
      });
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch attachments");
    }
  },
);

export const fetchInvoiceDetail = createAsyncThunk(
  "invoices/fetchInvoiceDetail",
  async (providerId: string, { rejectWithValue }) => {
    try {
      const data = await apiGet({
        endpoint: "/get-invoice-detail",
        params: { providerId },
        withAuth: true,
      });
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch invoice detail");
    }
  },
);

export const uploadLineItemFile = createAsyncThunk(
  "invoices/uploadLineItemFile",
  async (
    { file, invoiceId, lineItemId }: { file: File; invoiceId: string; lineItemId: string },
    { rejectWithValue },
  ) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const data = await apiPost({
        endpoint: "/upload-line-item-file",
        params: { invoiceId, lineItemId },
        data: formData,
        withAuth: true,
      });
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to upload file");
    }
  },
);

export const deleteLineItemFile = createAsyncThunk(
  "invoices/deleteLineItemFile",
  async (fileId: string, { rejectWithValue }) => {
    try {
      const data = await apiDelete({
        endpoint: "/delete-line-item-file",
        params: { id: fileId },
        withAuth: true,
      });
      return { ...data, fileId };
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to delete file");
    }
  },
);

const invoicesSlice = createSlice({
  name: "invoices",
  initialState,
  reducers: {
    selectInvoice: (state: any, action: PayloadAction<any>) => {
      state.selectedInvoice = action.payload;
    },
    selectInvoiceById: (state: any, action: PayloadAction<any>) => {
      const invoice = state.invoices.find((inv: any) => inv.id === action.payload);
      if (invoice) {
        state.selectedInvoice = invoice;
      } else {
        state.selectedInvoice = null;
      }
    },
    closeInvoiceDrawer: (state) => {
      state.selectedInvoice = null;
      state.invoiceAttachments = [];
    },
    changeInvoicesFilter: (state: any, action: PayloadAction<any>) => {
      (state.filters.invoices as any)[action.payload.name] = action.payload.value;
    },
    clearFilters: (state) => {
      state.filters = {
        invoices: { paymentStatus: ["1"] },
      };
    },
    clearMessages: (state) => {
      return {
        ...state,
        ...initialMessagesState,
      };
    },
    changeInvoiceData: (state: any, action: PayloadAction<any>) => {
      state.changes = {
        ...state.changes,
        [action.payload.name]: action.payload.value,
      };
    },
    setEditingState: (state: any, action: PayloadAction<any>) => {
      state.isEditingState = action.payload;
    },
    addAttachmentLocally: (state: any, action: PayloadAction<any>) => {
      state.invoiceAttachments.push(action.payload);
    },
    removeAttachmentLocally: (state: any, action: PayloadAction<string>) => {
      state.invoiceAttachments = state.invoiceAttachments.filter(
        (a: any) => a.id !== action.payload,
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInvoicesByProjectId.pending, (state) => {
        state.fetchInvoicesByProjectIdStatus = "loading";
      })
      .addCase(fetchInvoicesByProjectId.fulfilled, (state: any, action: PayloadAction<any>) => {
        state.fetchInvoicesByProjectIdStatus = "succeeded";
        state.invoices = action.payload;
        state.invoicesTotal = Array.isArray(action.payload) ? action.payload.length : 0;
        state.invoicesPage = 0;
      })
      .addCase(fetchInvoicesByProjectId.rejected, (state: any, action: PayloadAction<any>) => {
        state.fetchInvoicesByProjectIdStatus = "failed";
        state.fetchInvoicesByProjectIdError = action.payload;
      })
      .addCase(fetchInvoices.pending, (state) => {
        state.fetchInvoicesStatus = "loading";
      })
      .addCase(fetchInvoices.fulfilled, (state: any, action: PayloadAction<any>) => {
        state.fetchInvoicesStatus = "succeeded";
        if (Array.isArray(action.payload)) {
          // Legacy full-list response
          state.invoices = action.payload;
          state.invoicesTotal = action.payload.length;
          state.invoicesPage = 0;
        } else {
          // Server-paginated response
          state.invoices = action.payload?.items || [];
          state.invoicesTotal = action.payload?.total || 0;
          state.invoicesPage = Math.max(0, (action.payload?.page || 1) - 1);
          state.invoicesPageSize = action.payload?.pageSize || 10;
        }
      })
      .addCase(fetchInvoices.rejected, (state: any, action: PayloadAction<any>) => {
        state.fetchInvoicesStatus = "failed";
        state.fetchInvoicesError = action.payload;
      })
      .addCase(fetchPendingInvoices.pending, (state) => {
        state.fetchPendingInvoicesStatus = "loading";
      })
      .addCase(fetchPendingInvoices.fulfilled, (state: any, action: PayloadAction<any>) => {
        state.fetchPendingInvoicesStatus = "succeeded";
        state.pendingInvoices = action.payload;
      })
      .addCase(fetchPendingInvoices.rejected, (state: any, action: PayloadAction<any>) => {
        state.fetchPendingInvoicesStatus = "failed";
        state.fetchPendingInvoicesError = action.payload;
      })
      .addCase(chargeInvoice.pending, (state) => {
        state.invoiceChargeStatus = "loading";
        state.invoiceChargeError = null;
      })
      .addCase(chargeInvoice.fulfilled, (state: any) => {
        state.invoiceChargeStatus = "succeeded";
        state.invoiceChargeError = null;
        // Optionally update state after successful charge
      })
      .addCase(chargeInvoice.rejected, (state: any, action: PayloadAction<any>) => {
        state.invoiceChargeStatus = "failed";
        state.invoiceChargeError = action.payload;
      })
      .addCase(chargeCart.pending, (state) => {
        state.cartChargeStatus = "loading";
        state.cartChargeError = null;
      })
      .addCase(chargeCart.fulfilled, (state: any) => {
        state.cartChargeStatus = "succeeded";
        state.cartChargeError = null;
      })
      .addCase(chargeCart.rejected, (state: any, action: PayloadAction<any>) => {
        state.cartChargeStatus = "failed";
        state.cartChargeError = action.payload;
      })
      .addCase(setInvoiceVisibility.pending, (state) => {
        state.updateInvoiceStatus = "loading";
      })
      .addCase(setInvoiceVisibility.fulfilled, (state: any, action: PayloadAction<any>) => {
        state.updateInvoiceStatus = "succeeded";
        const updatedInvoiceId = action.payload?.providerId;
        state.invoices = state.invoices.map((invoice: any) =>
          invoice.id === updatedInvoiceId
            ? { ...invoice, isVisible: !invoice.isVisible }
            : invoice,
        );
      })
      .addCase(setInvoiceVisibility.rejected, (state: any, action: PayloadAction<any>) => {
        state.updateInvoiceStatus = "failed";
        state.updateInvoiceError = action.payload;
      })
      // Fetch attachments
      .addCase(fetchInvoiceAttachments.pending, (state) => {
        state.fetchAttachmentsStatus = "loading";
        state.fetchAttachmentsError = null;
      })
      .addCase(fetchInvoiceAttachments.fulfilled, (state: any, action: PayloadAction<any>) => {
        state.fetchAttachmentsStatus = "succeeded";
        state.invoiceAttachments = action.payload?.attachments || [];
      })
      .addCase(fetchInvoiceAttachments.rejected, (state: any, action: PayloadAction<any>) => {
        state.fetchAttachmentsStatus = "failed";
        state.fetchAttachmentsError = action.payload;
      })
      // Upload file
      .addCase(uploadLineItemFile.pending, (state) => {
        state.uploadFileStatus = "loading";
        state.uploadFileError = null;
      })
      .addCase(uploadLineItemFile.fulfilled, (state: any, action: PayloadAction<any>) => {
        state.uploadFileStatus = "succeeded";
        if (action.payload?.file) {
          state.invoiceAttachments.push(action.payload.file);
        }
      })
      .addCase(uploadLineItemFile.rejected, (state: any, action: PayloadAction<any>) => {
        state.uploadFileStatus = "failed";
        state.uploadFileError = action.payload;
      })
      // Delete file
      .addCase(deleteLineItemFile.pending, (state) => {
        state.deleteFileStatus = "loading";
        state.deleteFileError = null;
      })
      .addCase(deleteLineItemFile.fulfilled, (state: any, action: PayloadAction<any>) => {
        state.deleteFileStatus = "succeeded";
        state.invoiceAttachments = state.invoiceAttachments.filter(
          (a: any) => a.id !== action.payload?.fileId,
        );
      })
      .addCase(deleteLineItemFile.rejected, (state: any, action: PayloadAction<any>) => {
        state.deleteFileStatus = "failed";
        state.deleteFileError = action.payload;
      })
      // Fetch invoice detail
      // The row shows when the link was last sent, so patch it in place.
      .addCase(sendPaymentLink.fulfilled, (state: any, action: PayloadAction<any>) => {
        const { invoiceId, sentAt } = action.payload || {};
        const row = state.invoices.find((item: any) => item.id === invoiceId);
        if (row && sentAt) {
          row.paymentLinkSentAt = sentAt;
        }
      })
      .addCase(fetchInvoiceDetail.pending, (state) => {
        state.fetchDetailStatus = "loading";
        state.fetchDetailError = null;
      })
      .addCase(fetchInvoiceDetail.fulfilled, (state: any, action: PayloadAction<any>) => {
        state.fetchDetailStatus = "succeeded";
        // The detail is the invoice itself, while the row it replaces may carry
        // what the list added to it — the project it belongs to, the amount
        // still due, the project id the pay action needs. Merging keeps the
        // panel whole; replacing it blanked the project name the moment the
        // response landed.
        state.selectedInvoice =
          state.selectedInvoice && action.payload?.invoice
            ? { ...state.selectedInvoice, ...action.payload.invoice }
            : action.payload?.invoice || null;
      })
      .addCase(fetchInvoiceDetail.rejected, (state: any, action: PayloadAction<any>) => {
        state.fetchDetailStatus = "failed";
        state.fetchDetailError = action.payload;
      });
  },
});

export const {
  changeInvoicesFilter,
  clearFilters,
  selectInvoice,
  selectInvoiceById,
  closeInvoiceDrawer,
  clearMessages,
  changeInvoiceData,
  setEditingState,
  addAttachmentLocally,
  removeAttachmentLocally,
} = invoicesSlice.actions;

export default invoicesSlice.reducer;

// Emails the client a signed payment link for one invoice. The sent date comes
// back so the row can show it without a refetch.
export const sendPaymentLink = createAsyncThunk(
  "invoices/sendPaymentLink",
  async (
    { invoiceId, recipients, to }: { invoiceId: string; recipients?: string[]; to?: string },
    { rejectWithValue },
  ) => {
    try {
      const data: any = await sendInvoicePaymentLink(invoiceId, recipients, to);
      return { invoiceId, sentAt: data?.sentAt, to: data?.to, failed: data?.failed };
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to send the payment link");
    }
  },
);
